import {
  FALLBACK_CURRENCY,
  COUNTRY_CODE_US,
  COUNTRY_CODE_FR,
  COUNTRY_CODE_DE,
  COUNTRY_CODE_NO,
  COUNTRY_CODE_IT,
  COUNTRY_CODE_GB,
  CURRENCY_USD,
  CURRENCY_GBP,
  CURRENCY_NOK,
  CURRENCY_EUR,
  CULTURE_US_LOWER,
  CULTURE_GREAT_BRITAIN_LOWER,
  CULTURE_NORWAY_LOWER,
  CULTURE_ROUTE_PARAM,
  DISPLAY_EXCLUDED_CURRENCY_SYMBOLS,
} from '@constants/app.constants';
import {
  DATE_FORMAT_DATE_MONTH_YEAR,
  DATE_FORMAT_US,
  DATE_FORMAT_YEAR_MONTH_DATE,
  DATE_FORMAT_DATE_MONTH_YEAR_FRENCH,
  DATE_FORMAT_DATE_MONTH_YEAR_GERMAN,
  DATE_FORMAT_DATE_MONTH_YEAR_NORWEGIAN,
  DATE_FORMAT_DATE_MONTH_YEAR_ITALIAN,
} from '@constants/date-format.constants';
import { createSelector } from '@ngrx/store';
import { getCultureCodeParts } from '@utils/data-utils';
import { selectCultureFeatureState } from '../reducers';
import {
  selectCountries,
  selectCultures,
  selectCurrencies,
} from './resource.selector';
import { selectRouteParam } from './router.selector';

export const selectQuasiContinent = createSelector(
  selectCultureFeatureState,
  (state) => state.userQuasiContinent,
);

export const selectGeolocatedCountryCode = createSelector(
  selectCultureFeatureState,
  (state) => state.geolocatedCountryCode,
);
export const selectCurrentCultureCode = createSelector(
  selectRouteParam(CULTURE_ROUTE_PARAM),
  (cultureCode) => cultureCode,
);

export const selectCurrentCultureLanguageCode = createSelector(
  selectRouteParam(CULTURE_ROUTE_PARAM),
  (cultureCode) => getCultureCodeParts(cultureCode).language,
);

export const selectCurrentCultureCountryCode = createSelector(
  selectRouteParam(CULTURE_ROUTE_PARAM),
  (cultureCode) => getCultureCodeParts(cultureCode).country,
);

export const selectCurrentCultureName = createSelector(
  selectRouteParam(CULTURE_ROUTE_PARAM),
  selectCultures,
  (cultureCode, cultures) =>
    cultures?.find((c) => c.cultureCode === cultureCode)?.name ?? '',
);

export const selectCurrentCultureShortName = createSelector(
  selectRouteParam(CULTURE_ROUTE_PARAM),
  selectCultures,
  (cultureCode, cultures) =>
    cultures?.find((c) => c.cultureCode === cultureCode)?.shortName ?? '',
);

export const selectCurrentCultureCountry = createSelector(
  selectCurrentCultureCountryCode,
  selectCountries,
  (countryCode, countries) =>
    countries?.find((c) => c.countryCode === countryCode),
);

export const selectSelectedCurrencyCode = createSelector(
  selectCultureFeatureState,
  (state) => state.selectedCurrencyCode ?? FALLBACK_CURRENCY,
);

export const selectSelectedCurrencyPrefix = createSelector(
  selectSelectedCurrencyCode,
  selectCurrencies,
  (currencyCode, currencies) => {
    const match = currencies?.find(
      (c) => c.currencyCode === currencyCode && !DISPLAY_EXCLUDED_CURRENCY_SYMBOLS.some(e => e === c.currencyCode),
    );
    return match?.displayPrefix ?? '';
  },
);

export const selectSelectedCurrencyPrefixIncludeAll = createSelector(
  selectSelectedCurrencyCode,
  selectCurrencies,
  (currencyCode, currencies) => {
    const match = currencies?.find(
      (c) => c.currencyCode === currencyCode,
    );
    return match?.displayPrefix ?? '';
  },
);

export const selectSelectedCurrencySuffix = createSelector(
  selectSelectedCurrencyCode,
  selectCurrencies,
  (currencyCode, currencies) => {
    const match = currencies?.find(
      (c) => c.currencyCode === currencyCode && !DISPLAY_EXCLUDED_CURRENCY_SYMBOLS.some(e => e === c.currencyCode),
    );
    return match?.displaySuffix ?? '';
  },
);

export const selectCurrencyByCulture = createSelector(
  selectRouteParam(CULTURE_ROUTE_PARAM),
  (cultureCode) => {
    switch (cultureCode?.toLowerCase()) {
      case CULTURE_US_LOWER:
        return CURRENCY_USD;
      case CULTURE_GREAT_BRITAIN_LOWER:
        return CURRENCY_GBP;
      case CULTURE_NORWAY_LOWER:
        return CURRENCY_NOK;
    }
    return CURRENCY_EUR;
  },
);

export const selectCurrentCultureDateFormat = createSelector(
  selectCurrentCultureCountryCode,
  (countryCode) => {
    switch (countryCode) {
      case COUNTRY_CODE_US:
        return DATE_FORMAT_US;
      case COUNTRY_CODE_DE:
      case COUNTRY_CODE_FR:
      case COUNTRY_CODE_GB:
      case COUNTRY_CODE_IT:
      case COUNTRY_CODE_NO:
        return DATE_FORMAT_DATE_MONTH_YEAR;
    }
    return DATE_FORMAT_YEAR_MONTH_DATE;
  },
);

export const selectCurrentCultureDateDisplayFormat = createSelector(
  selectCurrentCultureCountryCode,
  (countryCode) => {
    switch (countryCode) {
      case COUNTRY_CODE_US:
        return DATE_FORMAT_US.toLowerCase();
      case COUNTRY_CODE_FR:
        return DATE_FORMAT_DATE_MONTH_YEAR_FRENCH.toLowerCase();
      case COUNTRY_CODE_DE:
        return DATE_FORMAT_DATE_MONTH_YEAR_GERMAN.toLowerCase();
      case COUNTRY_CODE_GB:
        return DATE_FORMAT_DATE_MONTH_YEAR.toLowerCase();
      case COUNTRY_CODE_IT:
        return DATE_FORMAT_DATE_MONTH_YEAR_ITALIAN.toLowerCase();
      case COUNTRY_CODE_NO:
        return DATE_FORMAT_DATE_MONTH_YEAR_NORWEGIAN.toLowerCase();
    }
    return DATE_FORMAT_YEAR_MONTH_DATE.toLowerCase();
  },
);
